import React, { useCallback } from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';
import type { DateRange } from 'venn-ui-kit';
import {
  DateRangePicker,
  ColorUtils,
  GetColor,
  Icon,
  ShimmerBlock,
  getAppTitle,
  FORECASTS_FAQ_HREF,
} from 'venn-ui-kit';
import { FREQUENCY_DATEPICKER_MODES, Dates, withSuspense, toEndOfPeriod } from 'venn-utils';
import { Section, PanelLabel, TextInline } from '../../../shared';
import { useRecoilState, useRecoilValue } from 'recoil';
import { rightPanelAnalysisSubjects, rightPanelDateRange, rightPanelSelectedBlock } from 'venn-state';

interface DateRangeSectionProps {
  readonly?: boolean;
}

/**
 * @deprecated this component will soon be replaced in Input Management V2.
 * If you make changes here, consider making similar changes to {@link AnalysisBlockTabV2}.
 */
const DateRangeSection = ({ readonly }: DateRangeSectionProps) => {
  const [computedDateRange, setComputedDateRange] = useRecoilState(rightPanelDateRange);
  const selectedBlock = useRecoilValue(rightPanelSelectedBlock);
  const subjects = useRecoilValue(rightPanelAnalysisSubjects);

  const onDateRangeChange = useCallback(
    // Align the dates to end of day to be consistent with the BE
    (newRange: DateRange) => {
      const range = {
        ...newRange,
        to: toEndOfPeriod(newRange.to, 'day'),
        from: toEndOfPeriod(newRange.from, 'day'),
      };
      setComputedDateRange((current) => ({ ...current, range }));
    },
    [setComputedDateRange],
  );

  const { range, frequency, maxRange } = computedDateRange;
  return (
    <Section>
      {selectedBlock && subjects[0] && selectedBlock.infoGraphicType === 'GROWTH_SIMULATION' ? (
        <Message>
          <Icon type={readonly ? 'exclamation-circle' : 'exclamation-triangle'} prefix="far" />
          <Text>
            Simulated Growth is based on the forecasts for {subjects[0].name}.
            <a target="_blank" rel="noopener noreferrer" href={FORECASTS_FAQ_HREF}>
              &nbsp; {`Learn more about forecasts in ${getAppTitle()}`}.
            </a>
          </Text>
        </Message>
      ) : (
        <></>
      )}
      <PanelLabel>
        Analysis Date Range:{' '}
        <TextInline style={{ display: readonly ? 'none' : 'inherit' }}>{capitalize(frequency)}</TextInline>
      </PanelLabel>
      <div>
        {readonly ? (
          <Text>
            {range.from ? Dates.toDDMMMYYYY(range.from, frequency) : ''} -{' '}
            {range.to ? Dates.toDDMMMYYYY(range.to, frequency) : ''} ({capitalize(frequency)})
          </Text>
        ) : (
          <DatePickerWrapper>
            <DateRangePicker
              value={range}
              range={maxRange}
              onChange={onDateRangeChange}
              granularity={FREQUENCY_DATEPICKER_MODES[frequency]}
              usePortal
            />
          </DatePickerWrapper>
        )}
      </div>
    </Section>
  );
};

export default withSuspense(<ShimmerBlock height={68} />, DateRangeSection);

const DatePickerWrapper = styled.div`
  display: inline-flex;
  min-height: 35px;
`;

const Text = styled.div`
  flex: 1;
`;

const Message = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 15px;
  font-size: 10px;
  border-color: ${GetColor.GreyScale.Grey30};
  margin-bottom: 10px;
  background: ${ColorUtils.opacifyFrom(GetColor.HighlightLight, 0.1)};
  column-gap: 10px;

  i {
    color: ${GetColor.HighlightDark};
    font-size: 14px;
  }

  button {
    color: ${GetColor.HighlightDark};
    min-height: auto;
    min-width: auto;
    padding: 0;
  }
`;
